/**
 * Actycrea: splitlines.esm.js (ES6)
 * MIT
 *
 * Splits any `[data-{PREFIX}-split]` text block into GSAP SplitText lines
 * and reveals them one by one on scroll with a zig-zag lateral entrance:
 * odd lines slide in from the left, even lines from the right, both fading
 * from opacity 0 to 1. Consumer: `module-basic` ( WYSIWYG paragraph, see
 * modules/_basic.php ).
 *
 * Re-splits on `onResizeEnd` ( see esm/resize.esm.js ): the paragraph
 * reflows at different viewport widths, so the previous line boundaries
 * ( and their per-line transforms ) go stale and the text looks misaligned
 * until it's split again against the new layout.
 *
 * Honors `prefers-reduced-motion`: when set, no SplitText / ScrollTrigger
 * instance is created and the text stays fully visible as authored.
 *
 * @module esm/splitlines
 */
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { PREFIX } from '../helpers/config.js';

gsap.registerPlugin( SplitText , ScrollTrigger );

const ATTR_ROOT     = `data-${PREFIX}-split`;
const SELECTOR_DATA = `[${ATTR_ROOT}]`;

const DEFAULTS = {
  distance: 80 ,          // px — horizontal offset each line travels in from
  duration: 0.8 ,         // s per line
  stagger: 0.15 ,         // s between consecutive lines
  ease: 'power3.out'
};

/**
 * Whether the shared `onResizeEnd` listener has already been bound
 * ( module-level guard so `initAll()` can be called more than once ).
 *
 * @type {boolean}
 */
let resizeBound = false;

export class SplitLines {
  /**
   * @param {HTMLElement} element - Text container to split and reveal.
   * @param {Object}      config  - Overrides for DEFAULTS ( distance, duration, stagger, ease ).
   */
  constructor( element , config = { } ){
    if( !(element instanceof HTMLElement) ) throw new Error('SplitLines: element inválido');

    this._config  = { ...DEFAULTS , ...config };
    this._element = element;
    this._split   = null;
    this._tween   = null;
    this._played  = false; // whether the entrance has already run once

    this._build( );
  }

  /**
   * Splits the element into lines ( GSAP SplitText ) and wires the reveal.
   *
   * - First run ( or after a resize, if it never played ): hides the lines
   *   in their alternating start position and wires the scroll-triggered
   *   entrance.
   * - Re-run after a resize once it already played: rebuilds the lines but
   *   leaves them instantly visible — the user already saw the entrance,
   *   a resize shouldn't hide already-read text again.
   *
   * @returns {void}
   */
  _build( ){
    const { distance , duration , stagger , ease } = this._config;

    this._split = SplitText.create( this._element , {
      type: 'lines' ,
      linesClass: 'split-line'
    } );

    if( !this._split.lines.length ) return;

    if( this._played ){
      gsap.set( this._split.lines , { opacity: 1 , x: 0 } );
      return;
    }

    // Odd lines ( index 0, 2, 4... ) enter from the left; even lines
    // ( index 1, 3, 5... ) enter from the right.
    gsap.set( this._split.lines , {
      opacity: 0 ,
      x: ( index ) => ( index % 2 === 0 ? -distance : distance )
    } );

    this._tween = gsap.to( this._split.lines , {
      opacity: 1 ,
      x: 0 ,
      duration ,
      stagger ,
      ease ,
      onComplete: ( ) => { this._played = true; } ,
      scrollTrigger: {
        trigger: this._element ,
        start: 'top 85%' ,
        once: true
      }
    } );
  }

  /**
   * Re-splits the text after a layout change ( viewport resize ): kills the
   * current tween/ScrollTrigger, reverts the stale SplitText markup and
   * rebuilds it from scratch against the new line wrapping, preserving
   * whether the entrance had already played.
   *
   * @returns {void}
   */
  refresh( ){
    if( this._tween && this._tween.scrollTrigger ) this._tween.scrollTrigger.kill( );
    if( this._tween ) this._tween.kill( );
    if( this._split ) this._split.revert( );

    this._tween = null;
    this._build( );
  }

  /**
   * Kills the ScrollTrigger/tween and reverts the SplitText instance,
   * restoring the original markup.
   *
   * @returns {void}
   */
  destroy( ){
    if( this._tween && this._tween.scrollTrigger ) this._tween.scrollTrigger.kill( );
    if( this._tween ) this._tween.kill( );
    if( this._split ) this._split.revert( );
  }

  /**
   * Collects every live SplitLines instance under `root`.
   *
   * @param {Document|HTMLElement} root
   * @returns {SplitLines[]}
   */
  static _instances( root = document ){
    return [ ...root.querySelectorAll( SELECTOR_DATA ) ]
      .map( ( el ) => el._splitLinesInstance )
      .filter( Boolean );
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    // Respect the user's motion preference: leave the text fully visible.
    if( window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ){
      return [ ];
    }

    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [ ];
    nodes.forEach( ( el ) => {
      if( el._splitLinesInstance ) return;
      el._splitLinesInstance = new SplitLines( el );
      instances.push( el._splitLinesInstance );
    } );

    if( instances.length > 0 && !resizeBound ){
      resizeBound = true;
      window.addEventListener( 'onResizeEnd' , ( ) => {
        SplitLines._instances( root ).forEach( ( instance ) => instance.refresh( ) );
        ScrollTrigger.refresh( );
      } );
    }

    return instances;
  }
}

export default SplitLines;
